import { ImageExportSettings } from './image-export.model';
import { ImageLayer } from './image-layer.model';

export type ImageEditorSourceKind = 'review' | 'social' | 'quick-edit' | 'demo';

export type ImageCropAspectRatio = 'free' | '1:1' | '4:5' | '9:16' | '16:9';

export interface ImageEditorSource {
  kind: ImageEditorSourceKind;
  id?: string | null;
  pipeline?: string | null;
}

export interface ImageSourceAsset {
  url: string;
  width?: number | null;
  height?: number | null;
  fileName?: string | null;
}

export interface ImageEditorMetadata {
  title: string;
  description: string;
}

export interface ImageEditorCanvasState {
  width: number;
  height: number;
  fabricJson: Record<string, unknown> | null;
}

export interface ImageFilterSettings {
  brightness: number;
  contrast: number;
  saturation: number;
  blur: number;
  duotone: string | null;
  hue?: number;
  /** -100 (cool) .. 100 (warm) white-balance shift. Optional — undefined/0 is a no-op. */
  temperature?: number;
  /** -100 (green) .. 100 (magenta) white-balance shift. Optional — undefined/0 is a no-op. */
  tint?: number;
  /** 0..100 radial edge darkening strength. Optional — undefined/0 is a no-op. */
  vignette?: number;
  /** 0..100 film-grain noise strength. Optional — undefined/0 is a no-op. */
  grain?: number;
  /** 0..100 convolution sharpen strength. Optional — undefined/0 is a no-op. */
  sharpen?: number;
}

export interface ImageEditorFilters {
  activeFilterId: string;
  cssFilter: string;
  settings: ImageFilterSettings;
}

export interface ImageEditorCrop {
  aspectRatio: ImageCropAspectRatio;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageEditorViewSettings {
  [key: string]: unknown;
  zoom: number;
  panMode: boolean;
  previewMode: boolean;
  showGrid: boolean;
  showGuides: boolean;
  guidesLocked: boolean;
  showCanvasBounds: boolean;
  showLayerBounds: boolean;
  showCropOverlay: boolean;
  showSafeAreas: boolean;
  panelsHidden: boolean;
}

export interface ImageEditorAppliedOption {
  id: string;
  toolId: string;
  section: string;
  label: string;
  appliedAt: string;
  commandType?: string | null;
  filterId?: string | null;
  targetLayerId?: string | null;
}

export interface ImageEditorOverlayAsset {
  id?: string | null;
  key?: string | null;
  url?: string | null;
  ref?: string | null;
  previewUrl?: string | null;
  dataUrl?: string | null;
  format?: string | null;
  mimeType?: string | null;
  aspectRatio?: string | null;
  width?: number | null;
  height?: number | null;
  isAnimated?: boolean | null;
}

export interface ImageEditorTemplate {
  id: string;
  name: string;
  description?: string | null;
  source?: string | null;
  overlayAsset?: ImageEditorOverlayAsset | null;
  config?: unknown;
}

export interface ImageProject {
  id: string;
  source: ImageEditorSource;
  image: ImageSourceAsset;
  metadata: ImageEditorMetadata;
  canvas: ImageEditorCanvasState;
  filters: ImageEditorFilters;
  crop: ImageEditorCrop;
  layers: ImageLayer[];
  selectedLayerId?: string | null;
  template: ImageEditorTemplate | null;
  export: ImageExportSettings;
  view: ImageEditorViewSettings;
  optionState: Record<string, ImageEditorAppliedOption>;
  createdAt?: string | null;
}

export const DEFAULT_IMAGE_FILTER_SETTINGS: ImageFilterSettings = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  blur: 0,
  duotone: null,
  hue: 0,
  temperature: 0,
  tint: 0,
  vignette: 0,
  grain: 0,
  sharpen: 0
};

export const DEFAULT_IMAGE_CROP: ImageEditorCrop = {
  aspectRatio: 'free',
  x: 0,
  y: 0,
  width: 1,
  height: 1
};

export const DEFAULT_IMAGE_VIEW_SETTINGS: ImageEditorViewSettings = {
  zoom: 1,
  panMode: false,
  previewMode: false,
  showGrid: false,
  showGuides: false,
  guidesLocked: false,
  showCanvasBounds: false,
  showLayerBounds: false,
  showCropOverlay: false,
  showSafeAreas: false,
  panelsHidden: false
};
